/**
 * Shared presentational field primitives for the Work Permit forms.
 * Pure markup over the .v3-wp-* classes — no business logic, so each sub-form
 * stays focused on its own state + onDataChange contract.
 */
import React, { useEffect, useRef } from 'react';

/**
 * Emit `data` to the parent whenever it changes, without depending on the
 * onDataChange identity — the parent can pass a fresh inline callback each
 * render without triggering an update loop (the V2 forms were fragile here).
 */
export function useEmit(data, onDataChange) {
    const cbRef = useRef(onDataChange);
    cbRef.current = onDataChange;
    useEffect(() => {
        if (cbRef.current) cbRef.current(data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);
}

export const Field = ({ label, required, full, children }) => (
    <div className={`v3-wp-field${full ? ' full' : ''}`}>
        {label && (
            <label className="v3-wp-label">
                {label}{required && <span className="v3-wp-req">*</span>}
            </label>
        )}
        {children}
    </div>
);

export const Text = ({ value, onChange, placeholder, type = 'text', ...rest }) => (
    <input
        className="v3-wp-input"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
    />
);

export const Area = ({ value, onChange, rows = 3, placeholder }) => (
    <textarea
        className="v3-wp-textarea"
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
    />
);

export const Select = ({ value, onChange, options = [], placeholder = 'Select…', disabled }) => (
    <select
        className="v3-wp-select"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
    >
        <option value="">{placeholder}</option>
        {options.map((o) => {
            const val = typeof o === 'string' ? o : o.value;
            const lbl = typeof o === 'string' ? o : o.label;
            return <option key={val} value={val}>{lbl}</option>;
        })}
    </select>
);

/** File input — captures the filename only (uploads deferred). */
export const File = ({ onChange, accept, multiple }) => (
    <input
        className="v3-wp-input"
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e) => onChange(multiple ? Array.from(e.target.files) : (e.target.files[0] || null))}
    />
);

export const Check = ({ label, checked, onChange, id }) => (
    <label className={`v3-wp-check${checked ? ' on' : ''}`} htmlFor={id}>
        <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        {label}
    </label>
);

/**
 * Repeatable-card wrapper for array sub-forms (contractors, workers, vehicles…).
 * Renders an Add button and per-row Delete/Clear footer controls.
 */
export const RepeatList = ({ title, items, onAdd, onRemove, onClear, addLabel, renderRow }) => (
    <>
        {items.map((item, index) => (
            <div className="v3-wp-rowcard" key={index}>
                <div className="v3-wp-rowcard-head">
                    <span className="v3-wp-rowcard-title">{title} {items.length > 1 ? index + 1 : ''}</span>
                </div>
                {renderRow(item, index)}
                <div className="v3-wp-rowfoot">
                    {index > 0 && (
                        <button type="button" className="v3-btn" onClick={() => onRemove(index)}>Delete</button>
                    )}
                    <button type="button" className="v3-btn" onClick={() => onClear(index)}>Clear</button>
                    {index === items.length - 1 && (
                        <button type="button" className="v3-btn v3-btn-primary" onClick={onAdd}>{addLabel}</button>
                    )}
                </div>
            </div>
        ))}
    </>
);
